import type { FieldItemStatus, OperatorMissionView } from '@groupe-reca/contracts'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  browserRunnerClock,
  DetectionRunner,
  type DetectionSetup,
  type DetectionSnapshot,
  type RunnerAction,
} from '@/features/detection/application/DetectionRunner'
import { allRulesOn, type EngineRules } from '@/features/detection/domain/rules'
import type { SiteStatus } from '@/features/detection/domain/types'
import type { GeolocationPort } from '@/platform/geolocation'
import { flagsFromView, type DetectionSource } from './detectionSource'
import type { ProfileOverride } from './profileOverride'

const siteStatus: Record<FieldItemStatus, SiteStatus> = {
  WAITING: 'TODO',
  COMPLETED: 'DONE',
  PROBLEM: 'PROBLEM',
}

export interface Detection {
  setup: DetectionSetup
  rules: EngineRules
  /** Profil remplacé localement par la calibration (jamais envoyé au serveur). */
  overridden: boolean
  snapshot: DetectionSnapshot
  dispatch: (action: RunnerAction) => void
}

/**
 * Moteur de détection de la mission ouverte, si le profil et les drapeaux sont disponibles et que le profil n'est pas
 * en mode OFF. `null` sinon : l'écran garde son comportement manuel. Un profil de calibration local (`override`)
 * remplace celui du serveur sans redémarrer le moteur.
 */
export function useDetection(
  view: OperatorMissionView | undefined,
  geolocation: GeolocationPort,
  source: DetectionSource,
  override: ProfileOverride | null = null,
): Detection | null {
  const missionId = view?.mission.id ?? null
  const open = view?.mission.status === 'IN_PROGRESS' || view?.mission.status === 'PAUSED'
  const paused = view?.mission.status === 'PAUSED'

  // Le snapshot est figé au démarrage : profil et drapeaux ne sont lus qu'une fois par mission.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- volontairement limité à l'identifiant de mission
  const serverSetup = useMemo(() => (view ? source.setup(view) : null), [missionId, source])
  // eslint-disable-next-line react-hooks/exhaustive-deps -- volontairement limité à l'identifiant de mission
  const flags = useMemo(() => (view ? flagsFromView(view) : null), [missionId])
  const profile = override?.profile ?? serverSetup?.profile ?? null
  const rules = override?.rules ?? allRulesOn
  const enabled = profile !== null && profile.mode !== 'OFF' && flags !== null

  const runner = useMemo(() => {
    if (!view || !enabled) return null
    return new DetectionRunner(
      { profile, flags },
      view.items.map((item) => ({ itemId: item.id, order: item.order, status: siteStatus[item.status] })),
      geolocation,
      browserRunnerClock,
      rules,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- un moteur par mission; profil et règles via reconfigure
  }, [enabled, flags, geolocation])

  useEffect(() => {
    if (runner && profile) runner.reconfigure(profile, rules)
  }, [runner, profile, rules])

  const [snapshot, setSnapshot] = useState<DetectionSnapshot | null>(runner?.snapshot ?? null)
  const [shownRunner, setShownRunner] = useState(runner)
  if (shownRunner !== runner) {
    setShownRunner(runner)
    setSnapshot(runner?.snapshot ?? null)
  }

  useEffect(() => runner?.subscribe(setSnapshot), [runner])

  useEffect(() => {
    if (!runner || !open) return undefined
    runner.start()
    return () => {
      runner.stop()
    }
  }, [runner, open])

  useEffect(() => {
    runner?.setPaused(paused)
  }, [runner, paused])

  const items = view?.items
  useEffect(() => {
    if (!runner || !items) return
    runner.syncStatuses(Object.fromEntries(items.map((item) => [item.id, siteStatus[item.status]])))
  }, [runner, items])

  const dispatch = useCallback(
    (action: RunnerAction) => {
      runner?.dispatch(action)
    },
    [runner],
  )

  if (!runner || !profile || !flags || !snapshot) return null
  return { setup: { profile, flags }, rules, overridden: override !== null, snapshot, dispatch }
}
